import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from '@app/core';
import { SharedModule } from '@app/shared';
import {
  TuiButton,
  TuiDialogContext,
  TuiError,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiButtonLoading,
  TuiFieldErrorPipe,
  TuiTextarea,
} from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { ContactFormService } from '../../../contact/services';

@Component({
  selector: 'app-pricing-contact-sales-dialog',
  imports: [
    SharedModule,
    TuiButton,
    TuiButtonLoading,
    TuiError,
    TuiFieldErrorPipe,
    TuiLabel,
    TuiTextarea,
    TuiTextfield,
  ],
  templateUrl: './pricing-contact-sales-dialog.component.html',
  styleUrl: './pricing-contact-sales-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingContactSalesDialogComponent {
  protected readonly context =
    inject<TuiDialogContext<void>>(POLYMORPHEUS_CONTEXT);
  protected readonly notificationsService = inject(NotificationsService);
  protected readonly contactFormService = inject(ContactFormService);

  protected readonly formGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    company: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    message: new FormControl('', [Validators.required]),
  });

  protected readonly submitContactForm = this.contactFormService.submit();

  protected handleCancel(): void {
    this.context.completeWith();
  }

  protected async handleSubmit(): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const formValue = this.formGroup.value;
    if (
      !formValue.firstName ||
      !formValue.lastName ||
      !formValue.company ||
      !formValue.email ||
      !formValue.message
    ) {
      return;
    }

    try {
      await this.submitContactForm.mutateAsync({
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        company: formValue.company,
        email: formValue.email,
        message: formValue.message,
      });

      void this.notificationsService.showSuccessNotification(
        'Your message has been sent successfully! We will get back to you soon.',
      );
      this.context.completeWith();
    } catch {
      void this.notificationsService.showErrorNotification(
        'Failed to submit your request. Please try again.',
      );
    }
  }
}
